import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaHandsHelping, FaPlus } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Claims.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (!input) return '--';
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const nowTimestamp = () => {
  const d = new Date();
  const pad = (n, l = 2) => String(n).padStart(l, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const formatCurrency = (amount) => {
  const n = parseFloat(amount);
  if (isNaN(n)) return 'KES 0.00';
  return `KES ${n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const msToTimestamp = (ms) => {
  const d = new Date(ms);
  const pad = (n, l = 2) => String(n).padStart(l, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

const statusClass = (status = '') => {
  const s = status.toLowerCase();
  if (s.includes('approv')) return 'approved';
  if (s.includes('reject') || s.includes('declin')) return 'rejected';
  if (s.includes('pend') || s.includes('review')) return 'pending';
  return 'default';
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Claims() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Table data ──
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Claim number ──
  const [claimNumber, setClaimnumber] = useState('');

  // ── Modal state ──
  const [modal, setModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Form fields ──
  const [natureOptions, setNatureOptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [policyNumber, setPolicynumber] = useState('');
  const [natureOfloss, setNatureofloss] = useState('');
  const [dateOfloss, setDateofloss] = useState('');
  const [claimAmount, setClaimamount] = useState('');

  const suggestionsRef = useRef(null);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(credentials ? { Authorization: `Basic ${credentials}` } : {}),
  }), [credentials]);

  // ══════════════════════════════════════
  //  CREDENTIALS
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch {
        toast.error('Failed to initialise authentication.');
      }
    })();
  }, []);

  // ══════════════════════════════════════
  //  AGENT FROM LOCAL STORAGE
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) {
      setAgent(user);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // ══════════════════════════════════════
  //  AGENT CODE
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, { headers: authHeaders() });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch {
        toast.error('Failed to load agent details.');
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  NATURE OF LOSS + ALL CLAIMS (for claim number)
  // ══════════════════════════════════════
  useEffect(() => {
    if (!isAuth) return;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API}/getNatureofloss`, { headers: authHeaders() }),
          fetch(`${API}/getAllclaims`, { headers: authHeaders() }),
        ]);
        const nature = await r1.json();
        const allClaims = await r2.json();
        setNatureOptions(Array.isArray(nature) ? nature : []);
        const cn = `ICN2025/000${(Array.isArray(allClaims) ? allClaims.length : 0) + 1}`;
        setClaimnumber(cn);
      } catch {
        // non-critical for table display
      }
    })();
  }, [isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  FETCH MY CLAIMS + CLIENTS
  // ══════════════════════════════════════
  const fetchClaims = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API}/getMyclaims?agent=${agentCode}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setClaims(Array.isArray(data) ? data : []);
    } catch {
      setFetchError('Failed to load claims. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [agentCode, isAuth, authHeaders]);

  useEffect(() => {
    if (!agentCode || !isAuth) return;
    fetchClaims();
    (async () => {
      try {
        const res = await fetch(`${API}/getClients?agent=${agentCode}`, { headers: authHeaders() });
        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
      } catch { /* non-critical */ }
    })();
  }, [agentCode, isAuth, authHeaders, fetchClaims]);

  // ══════════════════════════════════════
  //  FETCH CLIENT POLICIES when client selected
  // ══════════════════════════════════════
  useEffect(() => {
    if (!selectedClient || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getClientpolicies?client=${selectedClient}`, { headers: authHeaders() });
        const data = await res.json();
        setProposals(Array.isArray(data) ? data : []);
      } catch {
        toast.error('Failed to load client policies.');
      }
    })();
  }, [selectedClient, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  CLIENT SEARCH (local filter)
  // ══════════════════════════════════════
  const handleClientSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (val.length > 0) {
      const filtered = clients.filter(c =>
        c.FirstName?.toLowerCase().includes(val.toLowerCase()) ||
        c.Surname?.toLowerCase().includes(val.toLowerCase()) ||
        c.IdentificationNo?.toLowerCase().includes(val.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
      setSelectedClient(null);
    }
  };

  const handleClientSelect = (c) => {
    setSearchTerm(`${c.FirstName} ${c.Surname}`);
    setSelectedClient(c.OID);
    setSuggestions([]);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ══════════════════════════════════════
  //  TABLE FILTER
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    const q = filterText.toLowerCase();
    if (!q) return claims;
    return claims.filter(c =>
      (c.Dealer_name || '').toLowerCase().includes(q) ||
      (c.ProposalNo || '').toLowerCase().includes(q) ||
      (c.NatureOfClaimsDescriptions || '').toLowerCase().includes(q) ||
      (c.ClaimPositionDescription || '').toLowerCase().includes(q)
    );
  }, [claims, filterText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const currentRows = useMemo(() => filtered.slice(indexOfFirst, indexOfFirst + ROWS_PER_PAGE), [filtered, indexOfFirst]);
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // Reset to page 1 when filter changes
  useEffect(() => { setCurrentPage(1); }, [filterText]);

  // ══════════════════════════════════════
  //  OPEN / CLOSE MODAL
  // ══════════════════════════════════════
  const openModal = () => {
    setSearchTerm('');
    setSelectedClient(null);
    setSuggestions([]);
    setProposals([]);
    setPolicynumber('');
    setNatureofloss('');
    setDateofloss('');
    setClaimamount('');
    setModal(true);
  };

  // ══════════════════════════════════════
  //  SUBMIT CLAIM
  // ══════════════════════════════════════
  const submitClaim = async () => {
    if (!selectedClient) { toast.warn('Please select a client.'); return; }
    if (!policyNumber) { toast.warn('Please select a policy.'); return; }
    if (!natureOfloss) { toast.warn('Please select nature of loss.'); return; }
    if (!dateOfloss) { toast.warn('Please enter the date of loss.'); return; }
    if (!claimAmount || isNaN(claimAmount)) { toast.warn('Please enter a valid claim amount.'); return; }

    setSubmitting(true);
    try {
      const today = nowTimestamp();
      const res = await fetch(`${API}/postClaim`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ policyNumber, dateOfloss, natureOfloss, claimAmount, today, claimNumber, selectedClient, agentCode }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Claim submitted successfully!');
        setModal(false);
        fetchClaims();
      } else {
        toast.error('Failed to submit claim. Please try again.');
      }
    } catch {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="claims-page">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header userName={agentName || agent} />
      <Sidebar />

      <main className="claims-main">
        {/* Breadcrumb */}
        <div className="claims-top-row">
          <span className="claims-company">Definite Assurance</span>
          <span className="claims-breadcrumb">Dashboard &rsaquo; My Claims</span>
        </div>

        <div className="claims-card">
          <div className="claims-card-header">
            <h3><FaHandsHelping /> My Claims</h3>
            <button className="claims-new-btn" onClick={openModal}>
              <FaPlus /> New Claim
            </button>
          </div>

          {/* Toolbar */}
          <div className="claims-toolbar">
            <div className="claims-search-wrap">
              <FaSearch className="claims-search-icon" />
              <input
                type="text"
                placeholder="Filter by client, policy, nature…"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
              />
            </div>
            <span className="claims-stat">
              Total: <strong>{claims.length}</strong>
            </span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="claims-loading">
              <div className="claims-spinner" />
              <p>Loading claims…</p>
            </div>
          ) : fetchError ? (
            <div className="claims-error-state">
              <p>{fetchError}</p>
              <button onClick={fetchClaims}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="claims-empty">
              {filterText ? `No results for "${filterText}".` : 'No claims found. Click "New Claim" to get started.'}
            </div>
          ) : (
            <>
              <div className="claims-table-wrap">
                <table className="claims-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Client</th>
                      <th>Policy No.</th>
                      <th>Date of Loss</th>
                      <th>Nature of Loss</th>
                      <th>Claim Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, idx) => (
                      <tr key={val.OID ?? idx}>
                        <td>{indexOfFirst + idx + 1}</td>
                        <td>{val.Dealer_name ?? '--'}</td>
                        <td>{val.ProposalNo ?? '--'}</td>
                        <td>{formatISODate(val.DateOfLoss)}</td>
                        <td>{val.NatureOfClaimsDescriptions ?? '--'}</td>
                        <td>{formatCurrency(val.ClaimAmount)}</td>
                        <td>
                          <span className={`claims-status ${statusClass(val.ClaimPositionDescription)}`}>
                            {val.ClaimPositionDescription ?? 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="claims-pagination">
                <span className="claims-pagination-info">
                  Showing {indexOfFirst + 1}–{Math.min(indexOfFirst + ROWS_PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="claims-pagination-controls">
                  <button
                    className="claims-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="claims-page-ellipsis">…</span>
                      : <button
                          key={p}
                          className={`claims-page-btn${currentPage === p ? ' active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                  )}
                  <button
                    className="claims-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── New Claim Modal ── */}
      {modal && (
        <div className="claims-modal-overlay">
          <div className="claims-modal">
            <div className="claims-modal-header">
              <h4>New Claim &nbsp;<small style={{ color: '#888', fontWeight: 400, fontSize: '0.8rem' }}>{claimNumber}</small></h4>
              <button className="claims-modal-close" onClick={() => setModal(false)} aria-label="Close">&times;</button>
            </div>

            {/* Client search with autocomplete */}
            <div className="claims-form-group" ref={suggestionsRef}>
              <label>Client Name / ID Number *</label>
              <input
                type="text"
                placeholder="Search by first name, surname or ID…"
                value={searchTerm}
                onChange={handleClientSearch}
                autoComplete="off"
              />
              {suggestions.length > 0 && (
                <ul className="claims-suggestions">
                  {suggestions.map(c => (
                    <li key={c.OID} onClick={() => handleClientSelect(c)}>
                      {c.FirstName} {c.Surname} &mdash; {c.IdentificationNo}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Policy */}
            <div className="claims-form-group">
              <label>Policy *</label>
              <select value={policyNumber} onChange={e => setPolicynumber(e.target.value)}>
                <option value="" disabled>Select policy</option>
                {proposals.map(p => (
                  <option key={p.OID} value={p.OID}>{p.Narration} — {p.PolicyNo}</option>
                ))}
              </select>
            </div>

            {/* Nature of loss */}
            <div className="claims-form-group">
              <label>Nature of Loss *</label>
              <select value={natureOfloss} onChange={e => setNatureofloss(e.target.value)}>
                <option value="" disabled>Select nature of loss</option>
                {natureOptions.map(n => (
                  <option key={n.OID} value={n.OID}>{n.NatureOfClaimsDescriptions}</option>
                ))}
              </select>
            </div>

            {/* Date of loss */}
            <div className="claims-form-group">
              <label>Date of Loss *</label>
              <input
                type="datetime-local"
                onChange={e => setDateofloss(msToTimestamp(new Date(e.target.value).getTime()))}
              />
            </div>

            {/* Claim amount */}
            <div className="claims-form-group">
              <label>Claim Amount (KES) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={claimAmount}
                onChange={e => setClaimamount(e.target.value)}
              />
            </div>

            <button
              className="claims-submit-btn"
              onClick={submitClaim}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit Claim'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
